/**
 * The one place that says what a region's production status can be.
 *
 * Every surface that shows a status reads this: the pins, the cluster ring,
 * the cluster overview's groups, the Production status filter and the Region
 * status legend. Before this existed the list was spelled out separately in
 * each of them, which is how the legend, the filter and the ring ended up in
 * three different orders.
 *
 * Deliberately dependency-free so `node --test` can load it without a bundler.
 *
 * ## `available`
 *
 * `peak` and `disease` are in the design but the backend has no data for them
 * yet, so they are defined here and marked unavailable rather than left out.
 * That keeps their colour, label and position settled, and makes switching
 * them on a one-word edit instead of a hunt through six files.
 *
 * `available` is a statement about the *data source*, not about the rows
 * currently loaded. It governs what the UI offers — the legend's rows and the
 * filter's options — which must stay put as the user changes commodity. What
 * a given cluster happens to contain is a separate, narrower question, and the
 * overview answers it per cluster by only listing groups that have regions.
 */

/**
 * Display order, and the split the design draws a divider through: the first
 * run is "growing now or soon", the second is "winding down or not growing".
 *
 * `not_producing` carries its 70% opacity in the colour itself, as an 8-digit
 * hex (`B3` = 179/255). Every consumer hands this value straight to `fill`,
 * `stroke` or `backgroundColor`, so the alpha travels with it — the pin, the
 * legend swatch, the cluster ring and the filter chip cannot end up at
 * different opacities the way they would if each applied its own.
 */
export const GROWING_STATUS_GROUPS = [
  [
    { id: 'producing', label: 'Producing', color: '#2EDB7F', available: true },
    { id: 'peak', label: 'Peak', color: '#84CC16', available: false },
    { id: 'starting_soon', label: 'Starting soon', color: '#5FA68F', available: true },
  ],
  [
    { id: 'ending_soon', label: 'Ending soon', color: '#B39B2E', available: true },
    { id: 'not_producing', label: 'Not producing', color: '#A3AAA5B3', available: true },
    { id: 'disease', label: 'Disease', color: '#8B5CF6', available: false },
  ],
];

/** Every status, in design order, whether or not it has data. */
export const GROWING_STATUSES = GROWING_STATUS_GROUPS.flat();

/** The statuses the data can actually carry today — what the UI may offer. */
export const AVAILABLE_GROWING_STATUSES = GROWING_STATUSES.filter((s) => s.available);

/** Ids of the above, for filter option lists and URL validation. */
export const AVAILABLE_GROWING_STATUS_IDS = AVAILABLE_GROWING_STATUSES.map((s) => s.id);

/** Display order as ids, including the not-yet-available ones. */
export const GROWING_STATUS_ORDER = GROWING_STATUSES.map((s) => s.id);

const BY_ID = new Map(GROWING_STATUSES.map((s) => [s.id, s]));

/** A status's definition, or undefined if it is one we don't know. */
export const growingStatus = (id) => BY_ID.get(id);

/** A status's colour — grey rather than colourless for an unknown id. */
export const growingStatusColor = (id) => BY_ID.get(id)?.color || '#9CA3AF';

/** A status's label, falling back to the raw id so nothing renders blank. */
export const growingStatusLabel = (id) => BY_ID.get(id)?.label || id;

/** The legend's rows: design order and the divider, minus what has no data. */
export const availableGrowingStatusGroups = () =>
  GROWING_STATUS_GROUPS
    .map((group) => group.filter((s) => s.available))
    .filter((group) => group.length > 0);
